import type { Instant, Reminder, ReminderTarget } from "@hq/contracts";
import { newId } from "./ids";
import type { NowContext } from "./time";

export function createReminder(
  input: { id?: string; target: ReminderTarget; title: string; triggerAt: Instant },
  ctx: NowContext,
): Reminder {
  return {
    id: input.id ?? newId(),
    createdAt: ctx.now,
    updatedAt: ctx.now,
    revision: 0,
    target: input.target,
    title: input.title.trim(),
    triggerAt: input.triggerAt,
    status: "scheduled",
  };
}

export function rescheduleReminder(r: Reminder, triggerAt: Instant, ctx: NowContext): Reminder {
  const next: Reminder = { ...r, triggerAt, status: "scheduled", updatedAt: ctx.now };
  delete next.snoozedUntil;
  return next;
}

export function snoozeReminder(r: Reminder, snoozedUntil: Instant, ctx: NowContext): Reminder {
  return { ...r, status: "snoozed", snoozedUntil, updatedAt: ctx.now };
}

export function dismissReminder(r: Reminder, ctx: NowContext): Reminder {
  return { ...r, status: "dismissed", updatedAt: ctx.now };
}

export function markReminderDelivered(r: Reminder, ctx: NowContext): Reminder {
  return { ...r, status: "delivered", updatedAt: ctx.now };
}

export function deleteReminder(r: Reminder, ctx: NowContext): Reminder {
  return { ...r, deletedAt: ctx.now, updatedAt: ctx.now };
}

/** The instant a reminder should next fire, or undefined if it will not. */
export function nextFireAt(r: Reminder): Instant | undefined {
  if (r.deletedAt) return undefined;
  if (r.status === "scheduled") return r.triggerAt;
  if (r.status === "snoozed") return r.snoozedUntil ?? r.triggerAt;
  return undefined;
}

export function isReminderDue(r: Reminder, ctx: NowContext): boolean {
  const at = nextFireAt(r);
  return at !== undefined && at <= ctx.now;
}
